import {createAction} from '@reduxjs/toolkit'

import {OldHotelOfferEntity} from '../../../offer/business/offersMapping'
import {
  BaseGetRoomsParams,
  GetOffersParams,
  GetSearchParams,
  SearchData,
  TransformedRoomsResponse
} from '.'
import {SearchApiError} from './error'

interface OfferSearchPayloadBase {
  parameters: GetOffersParams
  searchId: string
}

// Note: these events could be refactored to simply have `OffersData` as the payload to simplify everything
// See search actions with `SearchData` below
interface OfferSearchStartedPayload extends OfferSearchPayloadBase {
  checkIn: string
  checkOut: string
  rooms?: string
}

export interface OfferSearchReceivedPayload extends OfferSearchPayloadBase {
  offers?: OldHotelOfferEntity
}

interface OfferSearchSucceededPayload extends OfferSearchPayloadBase {
  offers?: OldHotelOfferEntity
}

interface OfferSearchErroredPayload extends OfferSearchPayloadBase {
  error?: SearchApiError
}

interface RoomSearchSucceededPayload {
  roomsRequestId?: string
  params: BaseGetRoomsParams
}

// Added as part of: 1c2b1n6h-ap-rooms-long-polling
interface RoomSearchErroredPayload {
  roomsRequestId?: string
  params?: BaseGetRoomsParams
  data?: TransformedRoomsResponse
  error?: SearchApiError
}

interface RoomsSearchStartedPayload {
  roomsRequestId?: string
}

/** Action creator for when offers search starts */
export const offersSearchStarted = createAction<OfferSearchStartedPayload>(
  'searchApi/offersSearchStarted'
)
/** Action creator for when offers are received */
export const offersSearchReceived = createAction<OfferSearchReceivedPayload>(
  'searchApi/offersSearchRecieved'
)
/** Action creator for when offers search succeeded */
export const offersSearchSucceeded = createAction<OfferSearchSucceededPayload>(
  'searchApi/offersSearchSucceeded'
)

/** Action creator for when offers search errored */
export const offersSearchErrored = createAction<OfferSearchErroredPayload>(
  'searchApi/offerSearchErrored'
)

/** Action creator for when room search started */
export const roomSearchStarted = createAction<RoomsSearchStartedPayload>(
  'searchApi/roomSearchStarted'
)
/** Action creator for when room search succeeded */
export const roomSearchSucceeded = createAction<RoomSearchSucceededPayload>(
  'searchApi/roomSearchSucceeded'
)

/**
 * Added as part of: 1c2b1n6h-ap-rooms-long-polling
 * Action creator for when room search errored
 */
export const roomSearchErrored = createAction<RoomSearchErroredPayload>(
  'searchApi/roomSearchErrored'
)

interface SearchEventPayload {
  parameters: GetSearchParams
  data: SearchData
}

interface SearchErroredPayload {
  parameters: GetSearchParams
  data: SearchData | undefined
  error: SearchApiError
}

/** Action creator for when search started */
export const searchStarted = createAction<SearchEventPayload>(
  'searchApi/searchStarted'
)
/** Action creator for when search anchor is received */
export const searchAnchorReceived = createAction<SearchEventPayload>(
  'searchApi/searchAnchorReceived'
)
/** Action creator for when search hotels received */
export const searchHotelsReceived = createAction<SearchEventPayload>(
  'searchApi/searchHotelsReceived'
)
/** Action creator for when search offers received */
export const searchOffersReceived = createAction<SearchEventPayload>(
  'searchApi/searchOffersReceived'
)
/** Action creator for when search completed */
export const searchCompleted = createAction<SearchEventPayload>(
  'searchApi/searchCompleted'
)
/** Action creator for when search errored */
export const searchErrored = createAction<SearchErroredPayload>(
  'searchApi/searchErrored'
)

export type SearchApiGetSearchActionsType =
  | typeof searchStarted
  | typeof searchAnchorReceived
  | typeof searchHotelsReceived
  | typeof searchOffersReceived
  | typeof searchCompleted

export type SearchApiActionsType =
  | typeof offersSearchStarted
  | typeof offersSearchReceived
  | typeof offersSearchSucceeded
  | typeof offersSearchErrored
  | typeof roomSearchStarted
  | typeof roomSearchSucceeded
  | typeof roomSearchErrored
  | SearchApiGetSearchActionsType

export type FAQFetchSucceededPayload = {
  questionsAvailable: string[]
}

/** Action creator for when FAQ fetch succeeded */
export const faqFetchSucceeded = createAction<FAQFetchSucceededPayload>(
  'searchApi/faqFetchSucceeded'
)
